import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  Database,
  Check,
  CreditCard,
  Loader2,
  Shield,
  Zap,
  Coins,
  ArrowLeft,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const PaymentPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, getAuthHeader, logout } = useAuth();
  const [loading, setLoading] = useState(null); // index of loading package
  const [packages, setPackages] = useState([]);
  const [creditsRemaining, setCreditsRemaining] = useState(0);
  const [costPerQuery, setCostPerQuery] = useState(1);
  const [settings, setSettings] = useState(null);
  const [showLowCreditsNotice, setShowLowCreditsNotice] = useState(false);
  const packagesRef = useRef(null);

  useEffect(() => {
    const reason = location.state?.reason;
    const searchParams = new URLSearchParams(location.search);
    if (reason === 'low-credits' || searchParams.get('reason') === 'low-credits') {
      setShowLowCreditsNotice(true);
    }
  }, [location.state, location.search]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [settingsRes, creditsRes] = await Promise.all([
          axios.get(`${API_URL}/settings/public`),
          axios.get(`${API_URL}/credits/packages`, { headers: getAuthHeader(), withCredentials: true }).catch(() => null)
        ]);
        setSettings(settingsRes.data);
        if (creditsRes?.data) {
          setPackages(creditsRes.data.packages || []);
          setCreditsRemaining(creditsRes.data.credits_remaining || 0);
          setCostPerQuery(creditsRes.data.credit_cost_per_query || 1);
        } else {
          setPackages(settingsRes.data.credit_packages || []);
        }
      } catch (error) {
        console.error("Failed to fetch data");
      }
    };
    fetchData();
  }, []);

  const handlePurchase = async (packageIndex) => {
    setLoading(packageIndex);
    try {
      const response = await axios.post(
        `${API_URL}/credits/purchase`,
        { origin_url: window.location.origin, package_index: packageIndex },
        { headers: getAuthHeader() }
      );
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start checkout");
      setLoading(null);
    }
  };

  const handleDeclineTopUp = async () => {
    navigate('/', { replace: true });
    setTimeout(async () => {
      await logout();
    }, 100);
  };

  const siteName = settings?.site_name || "AIQueryStudio";
  const primaryColor = settings?.primary_color || "#22c55e";

  return (
    <div className="min-h-screen bg-[#09090b] flex flex-col p-4 md:p-8">
      <div className="w-full max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <Button variant="outline" className="gap-2" onClick={() => navigate(-1)} data-testid="back-to-app-btn">
            <ArrowLeft className="w-4 h-4" />
            Back to App
          </Button>
          <div className="flex items-center gap-2">
            <Coins className="w-6 h-6" style={{ color: primaryColor }} />
            <span className="font-bold text-lg">{siteName}</span>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
          <div className="text-sm text-muted-foreground" data-testid="decline-topup-caption">
            Choose a top-up package below or decline to return to the website.
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <Button
              size="lg"
              className="gap-2 w-full sm:w-auto font-semibold"
              style={{ backgroundColor: primaryColor, color: "#fff" }}
              onClick={() => packagesRef.current?.scrollIntoView({ behavior: 'smooth' })}
              data-testid="topup-cta-btn"
            >
              Continue to Purchase
            </Button>
            <Button
              variant="destructive"
              size="lg"
              className="w-full sm:w-auto"
              onClick={handleDeclineTopUp}
              data-testid="decline-topup-btn"
            >
              Decline and Log Out
            </Button>
          </div>
        </div>

        {showLowCreditsNotice && (
          <div className="mb-6 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-100" data-testid="low-credits-notice">
            You’re out of credits. Choose a top-up package below to keep working.
          </div>
        )}

        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold">Buy Credits</h1>
          <p className="text-muted-foreground mt-2">
            Each query costs {costPerQuery} credit{costPerQuery !== 1 ? 's' : ''}. Purchase credits to run unlimited queries.
          </p>
          {creditsRemaining > 0 && (
            <p className="text-sm mt-2">
              You currently have <strong className="text-primary">{creditsRemaining}</strong> credits remaining
            </p>
          )}
        </div>

        {/* Credit Packages Grid */}
        <div ref={packagesRef} className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8 max-w-3xl mx-auto" data-testid="credit-packages-grid">
          {packages.map((pkg, idx) => {
            const perCredit = pkg.credits > 0 ? (pkg.price / pkg.credits).toFixed(3) : 0;
            const isBest = idx === packages.length - 1; // Last package is best value
            return (
              <div
                key={idx}
                className={`relative bg-card border rounded-xl overflow-hidden transition-all hover:border-primary/50 flex flex-col ${
                  isBest ? 'border-primary ring-1 ring-primary/20 scale-105 shadow-lg shadow-primary/10 z-10' : 'border-border'
                }`}
              >
                {isBest && (
                  <div className="text-center py-1 text-[10px] font-bold uppercase tracking-wider" style={{ backgroundColor: primaryColor, color: '#000' }}>
                    BEST VALUE
                  </div>
                )}
                <div className="p-5 flex-1 flex flex-col justify-between space-y-4">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-foreground">{pkg.credits.toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground uppercase font-semibold tracking-wider mt-1">credits</p>
                  </div>
                  <div className="text-center py-3 border-y border-border/50">
                    <p className="text-2xl font-bold text-foreground">${pkg.price.toFixed(2)}</p>
                    <p className="text-[11px] text-muted-foreground mt-1">${perCredit} per credit</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs font-medium text-muted-foreground">
                      ~{Math.floor(pkg.credits / costPerQuery).toLocaleString()} queries
                    </p>
                  </div>
                  <Button
                    className="w-full gap-2 mt-2"
                    onClick={() => handlePurchase(idx)}
                    disabled={loading !== null}
                    style={isBest ? { backgroundColor: primaryColor, color: '#000' } : {}}
                    variant={isBest ? "default" : "outline"}
                    data-testid={`buy-package-${idx}`}
                  >
                    {loading === idx ? (
                      <><Loader2 className="w-4 h-4 animate-spin" />Processing</>
                    ) : (
                      <><CreditCard className="w-4 h-4" />Buy {pkg.label}</>
                    )}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        {/* What's included */}
        <div className="bg-card border border-border rounded-xl p-5 mb-6 max-w-3xl mx-auto">
          <h3 className="font-medium text-sm text-foreground mb-3 text-center">Everything included</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-4">
            {[
              "Unlimited database connections",
              "12 database types supported",
              "AI-powered query generation",
              "Query syntax validation",
              "Query history & saved queries",
              "CSV & JSON export",
              "Real-time metrics dashboard",
              "Full CRUD operations"
            ].map((feature, idx) => (
              <div key={idx} className="flex items-center gap-2 text-xs text-muted-foreground">
                <Check className="w-3 h-3 flex-shrink-0" style={{ color: primaryColor }} />
                {feature}
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-col items-center justify-center gap-2 text-xs text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <Shield className="w-3 h-3" />
            Secure payment powered by Stripe
          </div>
          <p>
            Purchasing as <span className="text-foreground">{user?.email}</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;
